import { rgba } from 'polished';
import styled from 'styled-components';
import capitalizeText from '#/utils/capitalizeText';

import { c07071D, c56EA99, cEA135E, cFFFFFF, mainGridGapColor } from '#/colors';

import { Loadable } from '#/components/shared/Loadable';
import {
  TradeTapeViewProvider,
  useTradeTrapeView,
} from '#/features/perpetuals/trades/trade-tape-view-context';
import { useTranslation } from '#/features/localization/utils';

import VolumeIndicationBase, { Indication } from './VolumeIndication';

export default function TradeTape() {
  return (
    <TradeTapeViewProvider>
      <TradeTapeContent />
    </TradeTapeViewProvider>
  );
}

function TradeTapeContent() {
  const { trades, status, error, loadRetryCount } = useTradeTrapeView();
  const { t } = useTranslation();

  if (status === 'error' && error.length > 0)
    return <Error data-test-load-retry-count={loadRetryCount}>{error}</Error>;

  const isLoading = status === 'loading' || status === 'disconnected';

  return (
    <Loadable isLoading={isLoading}>
      <Container
        role="grid"
        aria-readonly="true"
        data-test-load-retry-count={loadRetryCount}
      >
        <HeaderRow role="row">
          <span role="rowheader">{t('Price')}</span>
          <span role="rowheader">{t('Size')}</span>
          <span role="rowheader">{t('Time')}</span>
        </HeaderRow>

        {trades.map((trade) => (
          <Row key={trade.id} role="row" aria-label={rowAriaLabel(trade)}>
            <Price side={trade.side} role="gridcell">
              {trade.price}
            </Price>
            <span role="gridcell">{trade.size}</span>
            <span role="gridcell">{trade.createdTime}</span>
            <VolumeIndication
              width="90%"
              primary={trade.sizeRelativePercentage}
              side={trade.side}
            />
          </Row>
        ))}
      </Container>
    </Loadable>
  );
}

function rowAriaLabel(trade: {
  readonly side: string;
  readonly size: string;
  readonly price: string;
  readonly createdTime: string;
}) {
  return (
    `${capitalizeText(trade.side)} ${trade.size}` +
    ` @ ${trade.price} at ${trade.createdTime}`
  );
}

const VolumeIndication = styled(VolumeIndicationBase)`
  ${Indication} {
    opacity: 0.4;
  }
`;

const Container = styled.div`
  position: absolute;
  min-width: 100%;
  font-size: 11px;
`;

const BaseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  padding: 0 16px;
  align-items: center;
  height: 22px;
  color: ${rgba(cFFFFFF, 0.7)};
  font-family: 'Roboto Mono', monospace;

  & > * {
    justify-self: end;
  }
  & > *:nth-child(1) {
    justify-self: flex-start;
  }
`;

const HeaderRow = styled(BaseRow)`
  z-index: 1; // keeps header on top when scrolling
  position: sticky;
  top: 0;
  height: 32px;
  border-bottom: 1px solid ${mainGridGapColor};
  color: ${rgba(cFFFFFF, 0.5)};
  background-color: ${c07071D};
  font-family: unset;
  margin-bottom: 3px;
`;

const Row = styled(BaseRow)`
  position: relative;
`;

const Price = styled.span<{ side: 'BUY' | 'SELL' }>`
  color: ${({ side }) => (side === 'BUY' ? c56EA99 : cEA135E)};
`;

const Error = styled.div.attrs({ role: 'alert' })`
  height: 100%;
  display: grid;
  place-items: center;
  text-align: center;
  color: ${rgba(cEA135E, 0.7)};
  padding: 16px;
`;
